import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import sentinelIcon from "@/assets/sentinelhub-icon.svg";
import { useTheme } from "@/hooks/useTheme";

const SentinelHubGuard = () => {
  // Ensure theme system is initialized
  useTheme();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Helmet>
        <title>Política de Privacidade — SentinelHub Guard</title>
        <meta
          name="description"
          content="Política de privacidade da extensão SentinelHub Guard para Chrome e Microsoft Edge."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sentinelhub.com.br/sentinelhubguard" />
        <meta property="og:title" content="Política de Privacidade — SentinelHub Guard" />
        <meta
          property="og:description"
          content="Política de privacidade da extensão SentinelHub Guard para Chrome e Microsoft Edge."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sentinelhub.com.br/sentinelhubguard" />
        <meta name="twitter:title" content="Política de Privacidade — SentinelHub Guard" />
        <meta
          name="twitter:description"
          content="Política de privacidade da extensão SentinelHub Guard para Chrome e Microsoft Edge."
        />
      </Helmet>

      {/* Top nav */}
      <div className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao site
          </Link>
          <div className="inline-flex items-center gap-2">
            <img src={sentinelIcon} alt="SentinelHub Security Platform" className="h-6 w-6" />
            <span className="text-base font-bold tracking-tight">
              Sentinel<span className="text-primary">Hub</span>
              <span className="ml-1.5 text-muted-foreground font-normal">Guard</span>
            </span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="relative border-b border-border bg-gradient-to-br from-secondary/40 via-background to-background">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h1 className="text-3xl md:text-4xl font-display font-bold leading-tight mb-3">
            Política de <span className="text-primary">Privacidade</span>
          </h1>
          <p className="text-sm text-muted-foreground font-mono">
            Extensão para Google Chrome e Microsoft Edge
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Última atualização: 20 de abril de 2026 · Versão 1.4
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        {/* TLDR */}
        <section className="rounded-xl border-l-4 border-primary bg-primary/5 p-6">
          <h2 className="text-xs uppercase tracking-wider font-bold text-primary mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" /> Resumo em 30 segundos
          </h2>
          <p className="text-sm text-muted-foreground mb-2">
            <strong className="text-foreground">Nenhum dado pessoal é coletado.</strong> Nenhuma
            informação é enviada para servidores da SentinelHub.
          </p>
          <p className="text-sm text-muted-foreground mb-2">
            As únicas consultas externas feitas pela extensão enviam: (1) o nome do domínio que você
            está visitando para o serviço público <strong className="text-foreground">RDAP.org</strong>;
            (2) o endereço IP público do servidor desse domínio para o serviço{" "}
            <strong className="text-foreground">ipwho.is</strong>. Nenhum dado seu (nome, e-mail,
            histórico, conta, senha, cartão) acompanha essas consultas.
          </p>
          <p className="text-sm text-muted-foreground">
            Todo o resto acontece <strong className="text-foreground">100% localmente</strong> dentro
            do seu navegador.
          </p>
        </section>

        <Section title="1. Quem somos">
          <p>
            O <strong>SentinelHub Guard</strong> é uma extensão gratuita para navegadores
            desenvolvida pelo SentinelHub. A extensão analisa a segurança de sites que você visita e
            apresenta um veredito (nota A a F) para ajudar você a decidir se pode confiar no site.
          </p>
        </Section>

        <Section title="2. Informações que a extensão acessa">
          <p>
            Para funcionar, a extensão precisa acessar, apenas durante a navegação e apenas na
            memória do seu navegador:
          </p>
          <ul>
            <li><strong>URL da página ativa</strong> — para identificar o domínio a ser analisado</li>
            <li><strong>Cabeçalhos HTTP de resposta do site</strong> — para avaliar configurações de segurança (HSTS, CSP, X-Frame-Options, etc.)</li>
            <li><strong>Endereço IP público do servidor</strong> — para localização geográfica (GeoIP)</li>
            <li><strong>Cookies do domínio atual</strong> — para verificar se têm flags de proteção (Secure, HttpOnly, SameSite). O valor dos cookies não é lido.</li>
            <li><strong>Conteúdo do DOM da página</strong> (via content script) — apenas para detectar a presença de campos de senha e recursos HTTP em páginas HTTPS</li>
          </ul>

          <h3 className="text-base font-semibold text-foreground mt-6 mb-3">O que a extensão NÃO acessa</h3>
          <ul>
            <li>Suas senhas, dados de autopreenchimento ou credenciais</li>
            <li>Seu histórico de navegação além da aba ativa</li>
            <li>Dados de outras extensões</li>
            <li>Arquivos do seu computador</li>
            <li>Seu endereço IP pessoal ou localização física</li>
            <li>Informações de identidade, conta, documento ou pagamento</li>
          </ul>
        </Section>

        <Section title="3. Informações que saem do seu navegador">
          <p>
            Apenas duas consultas externas são feitas, e ambas contêm somente dados técnicos
            públicos do site visitado, nunca dados pessoais seus.
          </p>

          <ServiceCard
            title="3.1 rdap.org — consulta de registro de domínio"
            rows={[
              ["Enviado", "apenas o nome do domínio (ex: exemplo.com)"],
              ["Recebido", "data de registro, data de expiração, registrador, nameservers"],
              ["Finalidade", "mostrar a idade do domínio e quem o gerencia"],
            ]}
            policy={{ label: "about.rdap.org", href: "https://about.rdap.org" }}
          />

          <ServiceCard
            title="3.2 ipwho.is — geolocalização de IP"
            rows={[
              ["Enviado", "apenas o endereço IP público do servidor do site (não o seu IP)"],
              ["Recebido", "país, cidade, operadora (ISP/ASN), bandeira"],
              ["Finalidade", "mostrar onde o servidor do site está fisicamente localizado"],
            ]}
            policy={{ label: "ipwho.is/privacy", href: "https://ipwho.is/privacy" }}
          />

          <p className="mt-4">
            Ambas as consultas são <strong>cacheadas localmente</strong> no seu navegador por até 24
            horas (RDAP) e 7 dias (GeoIP), para reduzir chamadas externas e melhorar a velocidade.
          </p>
        </Section>

        <Section title="4. Armazenamento local">
          <p>
            A extensão usa <code className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-sm text-primary">chrome.storage.local</code>{" "}
            apenas para o cache descrito acima. Nenhuma informação é sincronizada entre
            dispositivos. Nenhum dado é enviado para servidores do SentinelHub.
          </p>
          <p>Você pode apagar todos os dados a qualquer momento removendo a extensão.</p>
        </Section>

        <Section title="5. Comunicação com a SentinelHub">
          <p>
            Nenhuma. A extensão não se conecta a servidores do SentinelHub ou de qualquer outro
            serviço externo além dos serviços públicos declarados na seção 3. Não há telemetria,
            analytics, rastreamento ou coleta de uso.
          </p>
        </Section>

        <Section title="6. Cookies">
          <p>
            A extensão lê cookies do domínio ativo apenas para inspecionar flags de segurança
            (Secure, HttpOnly, SameSite). O valor dos cookies não é lido, copiado, armazenado ou
            transmitido.
          </p>
          <p>A extensão não define cookies próprios em nenhum site.</p>
        </Section>

        <Section title="7. Publicidade e monetização">
          <p>
            O SentinelHub Guard é gratuito e não contém publicidade, tracking ou qualquer forma de
            monetização do usuário. Não vendemos, compartilhamos ou cedemos dados a terceiros porque
            não temos dados para ceder.
          </p>
        </Section>

        <Section title="8. Menores de idade">
          <p>
            A extensão não é direcionada a crianças e não coleta dados de nenhum usuário,
            independentemente da idade.
          </p>
        </Section>

        <Section title="9. Base legal (LGPD)">
          <p>
            Quando dados técnicos (nome de domínio, IP público do servidor) são enviados aos
            serviços RDAP.org e ipwho.is para prestação do serviço solicitado pelo próprio usuário,
            a base legal é a <strong>execução de contrato / prestação de serviço</strong> (Art. 7º,
            V da LGPD). Como não há tratamento de dados pessoais do titular, as demais obrigações de
            tratamento da LGPD não se aplicam a este fluxo.
          </p>
        </Section>

        <Section title="10. Mudanças nesta política">
          <p>
            Se houver mudanças relevantes, uma nova versão será publicada com data de atualização
            clara no topo desta página. O uso continuado da extensão após mudanças significa aceite
            da nova política.
          </p>
        </Section>

        <Section title="11. Contato">
          <p>Para dúvidas sobre esta política ou sobre a extensão, entre em contato:</p>
          <ul>
            <li><strong>Empresa:</strong> SentinelHub</li>
            <li>
              <strong>E-mail:</strong>{" "}
              <a href="mailto:contato@sentinelhub.com.br" className="text-primary hover:underline">
                contato@sentinelhub.com.br
              </a>
            </li>
            <li>
              <strong>Site:</strong>{" "}
              <a href="https://sentinelhub.com.br" className="text-primary hover:underline">
                sentinelhub.com.br
              </a>
            </li>
          </ul>
        </Section>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-8 text-center text-sm text-muted-foreground">
          © 2026 SentinelHub. Todos os direitos reservados. ·{" "}
          <Link to="/" className="text-primary hover:underline">
            Voltar ao site
          </Link>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className="text-xl md:text-2xl font-display font-bold text-foreground border-b border-border pb-2 mb-4">
      {title}
    </h2>
    <div className="space-y-3 text-muted-foreground leading-relaxed [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-2 [&_strong]:text-foreground">
      {children}
    </div>
  </section>
);

const ServiceCard = ({
  title,
  rows,
  policy,
}: {
  title: string;
  rows: [string, string][];
  policy: { label: string; href: string };
}) => (
  <div className="my-4 rounded-xl border border-border bg-secondary/30 p-5">
    <h3 className="text-base font-semibold text-foreground mb-3">{title}</h3>
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      {rows.map(([dt, dd]) => (
        <div key={dt} className="contents">
          <dt className="font-semibold text-foreground">{dt}:</dt>
          <dd className="text-muted-foreground">{dd}</dd>
        </div>
      ))}
      <dt className="font-semibold text-foreground">Política:</dt>
      <dd>
        <a href={policy.href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          {policy.label}
        </a>
      </dd>
    </dl>
  </div>
);

export default SentinelHubGuard;
